import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import StatusTag from '@/components/shared/Tags';
import HealthStatusCell, { HealthStatusKey } from './HealthStatusCell';
import { resolveOperationModeTag } from '@core/utils/statusUtils';
import { ItemStatus } from '@features/shopautomation/types.ts';

export interface OperationModeState {
    currentOperationMode: string;
    operationModeScheduleActive: boolean;
}

export interface ShopRow {
    id: string;
    name: string;
    street: string;
    city: string;
    shopId: string;
    healthStatus: HealthStatusKey;
    healthOrder: number;
    tooltipMessage: string;
    operationModeState: OperationModeState | null;
    items: ItemStatus[];
}

export const CurrentStateCell: React.FC<{ state: OperationModeState; t: (key: string) => string }> = ({ state, t }) => {
    const modeTag = resolveOperationModeTag(state.currentOperationMode);

    let scheduleLabel = t('manual');
    if (state.operationModeScheduleActive) scheduleLabel = t('timed');

    return (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <span className="scc-schedule-label">{scheduleLabel}</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {modeTag && <StatusTag config={modeTag} />}
            </Box>
        </Box>
    );
};

interface MobileShopCardProps {
    row: ShopRow;
    isExpanded: boolean;
    isSuperAdmin: boolean;
    hasShopAutomation: boolean;
    onToggle: (id: string) => void;
    onShopSetup: (id: string) => void;
    t: (key: string) => string;
}

const MobileShopCard: React.FC<MobileShopCardProps> = ({
    row,
    isExpanded,
    isSuperAdmin,
    hasShopAutomation,
    onToggle,
    onShopSetup,
    t
}) => {
    const divider = { borderBottom: '1px solid', borderColor: 'divider' };
    const labelSx = { minWidth: 110, color: 'text.secondary', flexShrink: 0 };

    return (
        <Box
            className="scc-mobile-card"
            onClick={() => onToggle(row.id)}
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '3px',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                mb: 1.5,
                cursor: 'pointer'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    ...divider
                }}
            >
                <Typography variant="body2" fontWeight={600}>
                    {row.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip
                        title={isExpanded ? t('collapseRowTooltip') : t('expandRowTooltip')}
                        classes={{ tooltip: 'page-title-tooltip' }}
                    >
                        <IconButton
                            size="small"
                            className="scc-icon-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggle(row.id);
                            }}
                        >
                            <i
                                className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}
                                style={{ lineHeight: 1, display: 'block' }}
                            />
                        </IconButton>
                    </Tooltip>
                    {isSuperAdmin && (
                        <Tooltip title={t('shopSettingsTooltip')} classes={{ tooltip: 'page-title-tooltip' }}>
                            <IconButton
                                size="small"
                                className="scc-icon-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onShopSetup(row.id);
                                }}
                            >
                                <i className="bi bi-gear" style={{ lineHeight: 1, display: 'block' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    minHeight: 40,
                    ...(isExpanded ? divider : {})
                }}
            >
                <Typography variant="body2" fontWeight={600} sx={labelSx}>
                    {t('state')}
                </Typography>
                <HealthStatusCell healthStatus={row.healthStatus} variant="shop" />
            </Box>

            {isExpanded && (
                <Box onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', px: 2, py: 1, minHeight: 40, ...divider }}>
                        <Typography variant="body2" fontWeight={600} sx={labelSx}>
                            {t('shopInfo')}
                        </Typography>
                        <Box>
                            <Typography variant="body2">{row.street}</Typography>
                            <Typography variant="body2">{row.city}</Typography>
                            {isSuperAdmin && row.shopId && (
                                <Typography variant="body2" color="text.secondary">
                                    ID: {row.shopId}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {hasShopAutomation && row.operationModeState?.currentOperationMode && (
                        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 3, pb: 1.5, minHeight: 56 }}>
                            <Typography variant="body2" fontWeight={600} sx={labelSx}>
                                {t('currentState')}
                            </Typography>
                            <CurrentStateCell state={row.operationModeState} t={t} />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default MobileShopCard;
